import React, { useState } from 'react';
import {
  X,
  Search,
  Star,
  StarOff,
  Users,
  FileText,
  Share2,
  ChevronRight,
  Mail,
  MessageSquare,
  Clock,
  CheckCircle,
  Zap,
  DollarSign,
  FileCheck,
  UserPlus,
  AlertCircle,
  RefreshCw,
  Heart,
  ClipboardList,
} from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

// FundElite-specific template categories
const TEMPLATE_CATEGORIES = [
  { id: 'all', name: 'All Templates', icon: FileText, count: 12 },
  { id: 'client-onboarding', name: 'Client Onboarding', icon: UserPlus, count: 2 },
  { id: 'consultation-followup', name: 'Consultation Follow-Up', icon: Users, count: 1 },
  { id: 'lead-nurture', name: 'Lead Nurture', icon: Heart, count: 1 },
  { id: 'document-requests', name: 'Document Request Reminders', icon: FileCheck, count: 2 },
  { id: 'credit-report', name: 'Credit Report Import Follow-Up', icon: FileText, count: 1 },
  { id: 'dispute-updates', name: 'Dispute Status Updates', icon: Zap, count: 2 },
  { id: 'billing', name: 'Billing Reminder', icon: DollarSign, count: 1 },
  { id: 'failed-payment', name: 'Failed Payment Recovery', icon: AlertCircle, count: 1 },
  { id: 're-engagement', name: 'Re-engagement', icon: RefreshCw, count: 1 },
  { id: 'review-request', name: 'Review / Testimonial Request', icon: Star, count: 1 },
  { id: 'staff-tasks', name: 'Staff Task Automation', icon: ClipboardList, count: 1 },
  { id: 'self-serve', name: 'Self-Serve Activation', icon: Zap, count: 1 },
];

const TEMPLATE_SOURCES = [
  { id: 'all', name: 'All Templates', icon: FileText },
  { id: 'my-templates', name: 'My Templates', icon: Users },
  { id: 'shared', name: 'Shared with Agency', icon: Share2 },
  { id: 'favorites', name: 'Favorites', icon: Star },
];

// Mock template data - FundElite-specific workflows
const WORKFLOW_TEMPLATES = [
  {
    id: 'tpl_001',
    name: 'New Client Welcome Sequence',
    category: 'client-onboarding',
    description: 'Welcome new clients with a personalized onboarding sequence including document requests and next steps.',
    trigger: 'Client Created',
    steps: 5,
    channels: ['email', 'sms'],
    tags: ['onboarding', 'welcome'],
    popular: true,
    useCount: 234,
  },
  {
    id: 'tpl_002',
    name: 'Consultation No-Show Follow-Up',
    category: 'consultation-followup',
    description: 'Automatically follow up with leads who missed their consultation appointment.',
    trigger: 'Consultation Missed',
    steps: 4,
    channels: ['email', 'sms'],
    tags: ['consultation', 'follow-up'],
    popular: true,
    useCount: 156,
  },
  {
    id: 'tpl_003',
    name: 'Missing Document Reminder',
    category: 'document-requests',
    description: 'Send automated reminders for pending document uploads with escalation.',
    trigger: 'Document Requested',
    steps: 3,
    channels: ['email', 'sms'],
    tags: ['documents', 'reminders'],
    popular: true,
    useCount: 312,
  },
  {
    id: 'tpl_004',
    name: 'Credit Report Imported Follow-Up',
    category: 'credit-report',
    description: 'Guide clients after they import their credit report with next steps and analysis preview.',
    trigger: 'Credit Report Imported',
    steps: 3,
    channels: ['email'],
    tags: ['credit-report', 'onboarding'],
    popular: false,
    useCount: 89,
  },
  {
    id: 'tpl_005',
    name: 'Dispute Round Submitted Update',
    category: 'dispute-updates',
    description: 'Notify clients when a new dispute round has been submitted with expected timeline.',
    trigger: 'Dispute Round Created',
    steps: 2,
    channels: ['email', 'sms'],
    tags: ['disputes', 'updates'],
    popular: true,
    useCount: 267,
  },
  {
    id: 'tpl_006',
    name: 'Dispute Results Notification',
    category: 'dispute-updates',
    description: 'Celebrate wins and explain results when dispute responses are received.',
    trigger: 'Dispute Status Changed',
    steps: 3,
    channels: ['email'],
    tags: ['disputes', 'results'],
    popular: false,
    useCount: 145,
  },
  {
    id: 'tpl_007',
    name: 'Payment Reminder Sequence',
    category: 'billing',
    description: 'Send friendly payment reminders before and on due dates.',
    trigger: 'Invoice Unpaid',
    steps: 4,
    channels: ['email', 'sms'],
    tags: ['billing', 'reminders'],
    popular: true,
    useCount: 198,
  },
  {
    id: 'tpl_008',
    name: 'Failed Payment Recovery',
    category: 'failed-payment',
    description: 'Recover failed payments with automated retry notifications and card update reminders.',
    trigger: 'Payment Failed',
    steps: 5,
    channels: ['email', 'sms'],
    tags: ['billing', 'recovery'],
    popular: false,
    useCount: 87,
  },
  {
    id: 'tpl_009',
    name: 'Self-Serve Trial Activation',
    category: 'self-serve',
    description: 'Guide self-serve users through their trial with tips and activation prompts.',
    trigger: 'Lead Created',
    steps: 6,
    channels: ['email'],
    tags: ['self-serve', 'activation'],
    popular: true,
    useCount: 156,
  },
  {
    id: 'tpl_010',
    name: 'Inactive Lead Re-Engagement',
    category: 're-engagement',
    description: 'Re-engage leads who went cold with value-driven content and special offers.',
    trigger: 'Tag Applied',
    steps: 5,
    channels: ['email'],
    tags: ['leads', 're-engagement'],
    popular: false,
    useCount: 78,
  },
  {
    id: 'tpl_011',
    name: 'Review Request After Milestone',
    category: 'review-request',
    description: 'Request reviews and testimonials after clients achieve credit score improvements.',
    trigger: 'Tag Applied',
    steps: 3,
    channels: ['email', 'sms'],
    tags: ['reviews', 'testimonials'],
    popular: false,
    useCount: 112,
  },
  {
    id: 'tpl_012',
    name: 'Staff Task Assignment Notification',
    category: 'staff-tasks',
    description: 'Notify staff members when tasks are assigned with due date reminders.',
    trigger: 'Task Created',
    steps: 2,
    channels: ['email'],
    tags: ['staff', 'tasks'],
    popular: false,
    useCount: 67,
  },
];

const ChannelIcon = ({ channel }) => {
  switch (channel) {
    case 'email':
      return <Mail size={14} className="text-blue-500" />;
    case 'sms':
      return <MessageSquare size={14} className="text-green-500" />;
    default:
      return null;
  }
};

const TemplateCard = ({ template, onSelect, isFavorite, onToggleFavorite }) => {
  return (
    <div 
      className="group bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all cursor-pointer overflow-hidden"
      onClick={() => onSelect(template)}
      data-testid={`template-card-${template.id}`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-slate-900 group-hover:text-teal-600 transition-colors">
                {template.name}
              </h3>
              {template.popular && (
                <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Popular</Badge>
              )}
            </div>
            <p className="text-sm text-slate-500 line-clamp-2">{template.description}</p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(template.id);
            }}
            className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            {isFavorite ? (
              <Star size={16} className="text-amber-500 fill-amber-500" />
            ) : (
              <StarOff size={16} className="text-slate-300 hover:text-slate-400" />
            )}
          </button>
        </div>
        
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <Zap size={12} />
            <span>{template.trigger}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock size={12} />
            <span>{template.steps} steps</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
          <div className="flex items-center gap-2">
            {template.channels.map((channel) => (
              <div key={channel} className="flex items-center gap-1 px-2 py-1 bg-slate-50 rounded text-xs">
                <ChannelIcon channel={channel} />
                <span className="capitalize text-slate-600">{channel}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <Users size={12} />
            <span>{template.useCount} uses</span>
          </div>
        </div>
      </div>
      
      <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {template.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-white rounded text-xs text-slate-500 border border-slate-200">
              {tag}
            </span>
          ))}
        </div>
        <Button size="sm" className="gap-1 h-8 text-xs">
          Use Template
          <ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
};

const WorkflowTemplateLibrary = ({ isOpen, onClose, onSelectTemplate }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [favorites, setFavorites] = useState(new Set(['tpl_001', 'tpl_005']));

  const toggleFavorite = (templateId) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      return next;
    });
  };

  const filteredTemplates = WORKFLOW_TEMPLATES.filter(template => {
    if (searchQuery && !template.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (selectedCategory !== 'all' && template.category !== selectedCategory) {
      return false;
    }
    if (selectedSource === 'favorites' && !favorites.has(template.id)) {
      return false;
    }
    return true;
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[min(1400px,95vw)] max-w-none max-h-[90vh] p-0 overflow-hidden" data-testid="template-library-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Workflow Template Library</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Choose a template to start building your workflow
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        <div className="flex h-[calc(90vh-80px)]">
          {/* Left Sidebar */}
          <div className="w-64 border-r border-slate-200 flex-shrink-0 overflow-y-auto">
            {/* Sources */}
            <div className="p-4 border-b border-slate-100">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Sources</h3>
              <nav className="space-y-1">
                {TEMPLATE_SOURCES.map((source) => (
                  <button
                    key={source.id}
                    onClick={() => setSelectedSource(source.id)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                      selectedSource === source.id
                        ? 'bg-teal-50 text-teal-700'
                        : 'text-slate-600 hover:bg-slate-50'
                    )}
                  >
                    <source.icon size={16} />
                    <span>{source.name}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Categories */}
            <div className="p-4">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Categories</h3>
              <nav className="space-y-1">
                {TEMPLATE_CATEGORIES.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={cn(
                      'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors',
                      selectedCategory === category.id
                        ? 'bg-teal-50 text-teal-700'
                        : 'text-slate-600 hover:bg-slate-50'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <category.icon size={16} />
                      <span className="truncate">{category.name}</span>
                    </div>
                    <span className="text-xs text-slate-400">{category.count}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Results Count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500">
                Showing {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
              </p>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  Popular First
                </Badge>
              </div>
            </div>

            {/* Template Grid */}
            {filteredTemplates.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="mx-auto text-slate-300 mb-4" size={48} />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No templates found</h3>
                <p className="text-sm text-slate-500">
                  Try adjusting your search or filters
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTemplates
                  .sort((a, b) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0))
                  .map((template) => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      onSelect={onSelectTemplate}
                      isFavorite={favorites.has(template.id)}
                      onToggleFavorite={toggleFavorite}
                    />
                  ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowTemplateLibrary;
